import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, Hash, FileText } from "lucide-react";
import PolicyholderCheckModal from "../../components/claims/PolicyholderCheckModal";
import policyService from "../../api/PolicyService";
import { buildClaimFormPolicy } from "../../utils/claimHelpers";
import { toast } from "react-toastify";
import Loader from "../../components/misc/Loader";
import EmptyState, { EmptyStatePresets } from "../../components/misc/EmptyState";

const StartClaim = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const presetForSelf = location.state?.forSelf;
  const [confirmPolicy, setConfirmPolicy] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPurchasedPolicies = async () => {
      setIsLoading(true);
      try {
        const response = await policyService.getPurchasedPolicies();
        if (response.status && response.data?.status_code === 200) {
          const purchasedPolicies = response.data.data || [];
          const enrichedPolicies = await Promise.all(
            purchasedPolicies.map(async (purchasedPolicy) => {
              try {
                const policyDetails = await policyService.getPolicy(
                  purchasedPolicy.insurance_policy_slug
                );
                if (policyDetails.status && policyDetails.data?.data) {
                  return {
                    ...purchasedPolicy,
                    insurance_policy: policyDetails.data.data,
                  };
                }
                return purchasedPolicy;
              } catch (error) {
                return purchasedPolicy;
              }
            })
          );
          setPolicies(enrichedPolicies);
        } else {
          toast.error(
            response.message || "An error occurred while fetching policies"
          );
        }
      } catch (error) {
        toast.error(
          error.response?.data?.reason ||
            "An error occurred while fetching policies"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchPurchasedPolicies();
  }, []);

  const continueWithPolicy = (policy, isPolicyholder) => {
    const mappedPolicy = buildClaimFormPolicy(policy);
    if (isPolicyholder) {
      navigate("/claim/new/form", {
        state: { policy: mappedPolicy, forSelf: true },
      });
      return;
    }
    navigate("/claim/new/relative", {
      state: { policy: mappedPolicy },
    });
  };

  const handlePolicySelect = (policy) => {
    if (typeof presetForSelf === "boolean") {
      continueWithPolicy(policy, presetForSelf);
      return;
    }
    setConfirmPolicy(policy);
  };

  if (isLoading) {
    return <Loader fullScreen={true} text="Loading policies..." />;
  }

  return (
    <div className="mx-auto max-w-8xl min-h-screen bg-white">
      <div className="pt-24 lg:pt-28" />
      <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
        <div className="flex gap-4 justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
              Start a claim
            </h1>
            <p className="mt-1 text-sm text-gray-600 sm:text-base">
              Select an active policy to proceed
            </p>
          </div>
        </div>

        {policies.length === 0 ? (
          <EmptyState
            {...EmptyStatePresets.noPolicies}
            onAction={() => navigate("/insurance/choose")}
            size="lg"
          />
        ) : (
          <div className="grid gap-5 mt-8 sm:grid-cols-2 lg:grid-cols-3">
            {policies.map((policy) => {
              const policyName =
                policy.insurance_policy?.policy_name ||
                policy.purchased_policy_data?.product_name ||
                "Policy";
              const policyNumber =
                policy.insurance_policy_slug?.slice(-8) || "N/A";
              return (
                <button
                  key={policy.insurance_policy_slug}
                  onClick={() => handlePolicySelect(policy)}
                  className="overflow-hidden relative p-5 text-left bg-white rounded-2xl border border-gray-100 shadow-sm transition group hover:shadow-md"
                >
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500 opacity-70" />
                  <div className="flex gap-3 items-center">
                    <div className="grid place-items-center w-10 h-10 text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl ring-2 shadow-md ring-emerald-500/30">
                      <ShieldCheck size={18} />
                    </div>
                    <div>
                      <div className="flex gap-2 items-center text-sm font-semibold text-gray-900">
                        <Hash size={15} /> #{policyNumber}
                      </div>
                      <p className="mt-1 text-xs text-gray-600">{policyName}</p>
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <span className="inline-flex items-center text-sm gap-2 rounded-full bg-gradient-to-r from-emerald-600 to-green-700 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm group-hover:scale-[1.02] transition">
                      <FileText size={14} /> Claim Policy
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <PolicyholderCheckModal
          open={!!confirmPolicy}
          onClose={() => setConfirmPolicy(null)}
          onConfirm={(isPolicyholder) => {
            if (!confirmPolicy) return;
            const selected = confirmPolicy;
            setConfirmPolicy(null);
            continueWithPolicy(selected, isPolicyholder);
          }}
          policyName={
            confirmPolicy?.insurance_policy?.policy_name ||
            confirmPolicy?.purchased_policy_data?.product_name
          }
        />
      </div>
    </div>
  );
};

export default StartClaim;
